/* eslint-disable import/no-extraneous-dependencies */
import { IconDefinition } from '@fortawesome/fontawesome-common-types';
// eslint-disable-next-line import-x/no-cycle
import { Entry, ENTRY_OBJECT_TYPES } from 'quotient/DirectoryOrganizer/types';
import { BlueprintType } from 'quotient/types';
import { ReactNode } from 'react';
import { TreeInformation, TreeItem, TreeItemRenderContext } from 'react-complex-tree';

export type StringifiedUUID = string;

export type ActiveNodeColor =
  | 'tertiary.200'
  | 'quaternary.200'
  | 'primary.200'
  | 'primary.50'
  | 'secondary.200'
  | 'tertiary.50'
  | 'quaternary.50';
export type NodeTitleBGColor = 'primary.200' | 'secondary.200' | 'tertiary.200' | 'quaternary.200';
export type NodeTitleBGHoverColor = 'primary.100' | 'secondary.50' | 'tertiary.50' | 'quaternary.50';

export enum AttributeWorksheetPKTypeChoices {
  EMPLOYEE = 'employee',
  PLAN_PERIOD_GROUP = 'plan_period_group',
  CUSTOM = 'custom',
}

export type NavigatorModelTreeNode = {
  id: StringifiedUUID;
  is_root: boolean;
  index: string;
  children: Array<string>;
  data: NavigatorDataObject;
};

export type RenderTreeItemProps<T> = {
  arrow?: ReactNode;
  children?: ReactNode;
  context: TreeItemRenderContext;
  depth?: number;
  info: TreeInformation;
  item: TreeItem<T>;
  title?: ReactNode;
};

export type NavigatorDataObject = {
  object: string;
  id: StringifiedUUID;
  name: string;
  type: WORKSHEET_TYPES | ENTRY_OBJECT_TYPES | 'root';
  description?: string;
  is_hidden?: boolean;
  workbook_id?: StringifiedUUID;
  component_instance_id?: StringifiedUUID;
  componentGroupColor?: string;
  object_id?: StringifiedUUID;
  object_type?: ENTRY_OBJECT_TYPES;
  component_id?: StringifiedUUID;
  blueprint_instance_id?: StringifiedUUID;
  blueprint_instance_type?: BlueprintType;
  is_read_only?: boolean;
  directory_id?: StringifiedUUID;
  root_directory_id?: StringifiedUUID;
  parent_object_type?: string;
  parent_object_id?: StringifiedUUID;
};

export enum WORKSHEET_TYPES {
  API = 'api',
  RAW = 'raw',
  DERIVED = 'derived',
  PAYOUT_SUMMARY = 'payout_summary',
  PERIOD_GROUP = 'period_group',
  REPORT_MODEL = 'report_model',
  HIERARCHY = 'hierarchy',
  HISTORICAL_DETAILED = 'historical_detailed',
  HISTORICAL_PAYOUTS = 'historical_payouts',
  OUTPUT = 'output',
  QUOTA_WORKSHEET = 'quota',
  DATA_FLOW = 'data_flow',
  UEA = 'unified_employee_assumption',
  MASTER = 'master',
  ATTRIBUTE = 'attribute',
  COMPONENT_INSTANCE = 'component_instance',
  PAYOUT_SETTINGS = 'payout_settings',
  BLUEPRINT_INSTANCE = 'blueprint_instance',
  BLUEPRINT_INSTANCE_TYPE_CREDITING = 'crediting',
  OBJECT = 'object',
  WORKSPACE_MODEL = 'workspace_model',
}

export enum PlanVizNodeTypes {
  OUTPUT = 'output',
  CREDITS = 'credits',
  WORKSHEET = 'worksheet',
  INPUT = 'input',
  BLUEPRINT = 'blueprint',
}

export type NavigatorWorksheet = Partial<Entry> & {
  object: string;
  id: StringifiedUUID;
  name: string;
  type: WORKSHEET_TYPES;
  description?: string;
  pk_type?: AttributeWorksheetPKTypeChoices;
  is_hidden?: boolean;
  workbook_id?: StringifiedUUID;
  blueprint_instance_id?: StringifiedUUID;
  blueprint_instance_type?: BlueprintType;
  component_instance_id?: StringifiedUUID;
  component_id?: StringifiedUUID;
  columns_width?: { [key: string]: number } | null;
  records_per_page?: number;
  worksheet_name?: string; // needed for historical detailed worksheets
  worksheet_uuid?: string; // needed for historical detailed worksheets
  snapshottable_sources?: Array<any>;
  icon?: IconDefinition;
};

export type MenuGroupOption = 'default' | 'additive' | 'destructive';
export const DEFAULT_GROUPING = 'default';

export type SearchMenuItem = {
  id: string;
  name: string;
  icon?: IconDefinition;
  componentGroupColor?: string;
};

export type SearchMenuGroup = {
  name: string;
  default: boolean;
  menuItems: SearchMenuItem[];
};

export enum WORKSHEETS_ORDERING_TYPES {
  alphabetical = 'alphabetical',
  custom = 'custom',
}

export enum WORKBOOK_TYPES {
  COMPONENT = 'component',
  CALCULATION = 'calculation',
  CALCULATION_MODEL = 'calculation_model',
  DATA = 'data',
  MASTER = 'master',
  TRANSFORMATION = 'transformation',
  ATTRIBUTE = 'attribute',
  WORKSPACE_MODEL = 'workspace_model',
  OBJECT = 'object',
  HIERARCHY = 'hierarchy',
}
